import Button from "@app/components/button";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import { addToast } from "@app/components/toast/toast.service";
import { MAX_SIZE_UPLOAD, SystemMessage, USER_INFO_KEY } from "@app/constants";
import UserService from "@app/services/http/user.service";
import { selectAuth, storeUser } from "@app/store/auth";
import { ChangeInfoRequest, ChangePasswordRequest } from "@app/types";
import {
  changeInfoValidationSchema,
  changePasswordValidationSchema,
} from "@app/validations";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import StorageService from "@core/services/storage";
import clsx from "clsx";
import { Formik, FormikContextType, Form } from "formik";
import { ChangeEvent, createRef, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

enum AccountTab {
  INFO = 1,
  CHANGE_PASSWORD = 2,
}

function UserInfo() {
  const formRef = createRef<FormikContextType<ChangeInfoRequest>>();

  const inputThumbnailRef = useRef<HTMLInputElement>(null);

  const [errorMessage, setErrorMessage] = useState<{
    thumbnail: string;
  }>({
    thumbnail: "",
  });

  const dispatch = useDispatch();

  const [value, forceUpdate] = useForceUpdate();
  const { abortController } = useCancellationRequest();

  const { email, fullName, phone, thumbnail } = useSelector(selectAuth);

  useEffect(() => {
    if (value === 0) {
      formRef.current?.setValues({
        email,
        fullName,
        phone,
      });
    }
  }, [value, email, fullName]);

  useEffect(() => {
    if (value === 0) {
      UserService.getInfo(abortController.signal).then((res) => {
        StorageService.setObject(USER_INFO_KEY, res.data);
        dispatch(storeUser(res.data));
      });
    }

    if (value >= 1) {
      UserService.getInfo(abortController.signal).then((res) => {
        StorageService.setObject(USER_INFO_KEY, res.data);
        dispatch(storeUser(res.data));
        formRef.current?.setValues({
          email: res.data.email,
          fullName: res.data.fullName,
          phone: res.data.phone,
        });
      });
    }
  }, [value]);

  const onSubmit = (values: ChangeInfoRequest) => {
    UserService.changeInfo({
      email: values.email,
      fullName: values.fullName,
      phone: values.phone,
    }).then(() => {
      addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
      forceUpdate();
    });
  };

  const onChangeThumbnailFileClick = () => {
    if (inputThumbnailRef.current) {
      inputThumbnailRef.current.click();
    }
  };

  const handleThumbnailFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) {
      const newErrorMessage = {
        thumbnail: SystemMessage.INPUT_REQUIRED,
      };

      setErrorMessage(newErrorMessage);

      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      const newErrorMessage = {
        thumbnail: SystemMessage.MAX_SIZE_UPLOAD,
      };

      setErrorMessage(newErrorMessage);

      return;
    }

    const newErrorMessage = {
      thumbnail: "",
    };

    setErrorMessage(newErrorMessage);

    UserService.updateThumbnail(file).then(() => {
      const newErrorMessage = {
        thumbnail: "",
      };

      setErrorMessage(newErrorMessage);

      addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
      forceUpdate();
    });
  };

  return (
    <div className="flex xl:flex-row lg:flex-row md:flex md:flex-col">
      <div>
        <div className="flex flex-col items-center mt-10">
          <Formik
            innerRef={formRef}
            initialValues={{ fullName: "", email: "", phone: "" }}
            onSubmit={onSubmit}
            validateOnBlur
            validateOnChange
            validationSchema={changeInfoValidationSchema}
            enableReinitialize
          >
            <Form>
              <div className="font-semibold text-sm font-[SVN-Gilroy]">
                Họ và tên
              </div>
              <FormControl name="fullName">
                <Input
                  className="mt-1 !w-[397px]"
                  inputClassName="!bg-[#DFE2E7] !rounded-xl font-[SVN-Gilroy] font-medium !h-12 !leading-12"
                />
              </FormControl>
              <div className="font-semibold text-sm mt-5 font-[SVN-Gilroy]">
                Email
              </div>
              <FormControl name="email">
                <Input
                  className="mt-1 !w-[397px]"
                  inputClassName="!bg-[#DFE2E7] !rounded-xl font-[SVN-Gilroy] font-medium !h-12 !leading-12"
                />
              </FormControl>
              <div className="font-semibold text-sm mt-5 font-[SVN-Gilroy]">
                Số điện thoại (Không bắt buộc)
              </div>
              <FormControl name="phone">
                <Input
                  className="mt-1 !w-[397px]"
                  inputClassName="!bg-[#DFE2E7] !rounded-xl font-[SVN-Gilroy] font-medium !h-12 !leading-12"
                />
              </FormControl>
              <div className="w-[397px] flex justify-center">
                <hr className="mt-4 h-0.5 bg-[#C0C5CF] w-[341px]" />
              </div>
              <div className="flex mt-4">
                <Button
                  width="auto"
                  type="submit"
                  className="!w-[94px] !h-9 font-bold bg-gradient-to-b from-[#8092e4] to-[#38499b] hover:from-[#8997dd] hover:to-[#4559be] !rounded-[20px] mr-4"
                  label="Cập nhật"
                  labelClassName="text-sm"
                />
                <Button
                  width="auto"
                  className="!w-[57px] !h-9 font-bold !bg-[#DFE2E7] hover:!bg-[#f3f5f7] !rounded-[20px]"
                  label="Hủy"
                  labelClassName="text-sm text-[#4E586C]"
                />
              </div>
            </Form>
          </Formik>
        </div>
      </div>
      <div className="mt-12 xl:ml-36 lg:ml-24 md:ml-32 flex flex-col items-center">
        <div className="w-[100px] h-[100px] rounded-full overflow-hidden">
          <img className="w-full h-full object-cover" src={thumbnail} alt="" />
        </div>
        <input
          ref={inputThumbnailRef}
          type="file"
          name="thumbnailFile"
          hidden
          onChange={handleThumbnailFileChange}
        />
        {errorMessage.thumbnail && (
          <div className="flex error-color text-xs">
            {errorMessage.thumbnail}
          </div>
        )}
        <Button
          width="auto"
          onClick={onChangeThumbnailFileClick}
          theme="none"
          label="Thay ảnh"
          className="!w-[92px] text-[14px] !h-9 font-bold border border-[#38499b] hover:bg-[#4559be] hover:text-white !rounded-[20px] mt-4"
        />
      </div>
    </div>
  );
}

function ChangePassword() {
  const formRef = createRef<FormikContextType<ChangePasswordRequest>>();

  const onSubmit = (values: ChangePasswordRequest) => {
    UserService.changePassword(values.oldPassword, values.newPassword).then(
      () => {
        addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
        formRef.current?.setValues({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        formRef.current?.resetForm();
      }
    );
  };

  return (
    <div>
      <Formik
        innerRef={formRef}
        initialValues={{
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        }}
        onSubmit={onSubmit}
        validateOnBlur
        validateOnChange
        validationSchema={changePasswordValidationSchema}
      >
        <Form>
          <div className="mt-10">
            <FormControl name="oldPassword">
              <Input
                type="password"
                className="!w-[397px]"
                inputClassName="!bg-[#DFE2E7] !rounded-xl font-[SVN-Gilroy] font-medium !h-12 !leading-12"
                placeholder="Nhập mật khẩu hiện tại"
                inputIconClassName="!w-[18px] !h-[18px]"
              />
            </FormControl>
          </div>
          <div className="mt-5">
            <FormControl name="newPassword">
              <Input
                type="password"
                className="!w-[397px]"
                inputClassName="!bg-[#DFE2E7] !rounded-xl font-[SVN-Gilroy] font-medium !h-12 !leading-12"
                placeholder="Mật khẩu mới"
                inputIconClassName="!w-[18px] !h-[18px]"
              />
            </FormControl>
          </div>
          <div className="mt-5">
            <FormControl name="confirmPassword">
              <Input
                type="password"
                className="!w-[397px]"
                inputClassName="!bg-[#DFE2E7] !rounded-xl font-[SVN-Gilroy] font-medium !h-12 !leading-12"
                placeholder="Nhập lại mật khẩu mới"
                inputIconClassName="!w-[18px] !h-[18px]"
              />
            </FormControl>
          </div>
          <div className="w-[397px] flex justify-center">
            <hr className="mt-4 h-0.5 bg-[#C0C5CF] w-[341px]" />
          </div>
          <div className="mt-4 flex">
            <Button
              width="auto"
              type="submit"
              className="!w-[94px] !h-9 font-bold bg-gradient-to-b from-[#8092e4] to-[#38499b] hover:from-[#8997dd] hover:to-[#4559be] !rounded-[20px] mr-4"
              label="Cập nhật"
              labelClassName="text-sm"
            />
            <Button
              width="auto"
              className="!w-[57px] !h-9 font-bold !bg-[#DFE2E7] hover:!bg-[#f3f5f7] !rounded-[20px]"
              label="Hủy"
              labelClassName="text-sm text-[#4E586C]"
            />
          </div>
        </Form>
      </Formik>
    </div>
  );
}

function AccountUser() {
  const [activedTab, setActivedTab] = useState(AccountTab.INFO);

  const handleTabClick = (tab: AccountTab) => {
    setActivedTab(tab);
  };

  const render = () => {
    switch (activedTab) {
      case AccountTab.INFO:
        return <UserInfo />;
      case AccountTab.CHANGE_PASSWORD:
        return <ChangePassword />;
    }
  };

  return (
    <div>
      <div className="flex gap-10 mt-4">
        <div
          className={clsx("cursor-pointer font-medium font-[SVN-Gilroy]", {
            "text-[#38499b]": activedTab === AccountTab.INFO,
          })}
          onClick={() => handleTabClick(AccountTab.INFO)}
        >
          Thông tin cá nhân
        </div>
        <div
          className={clsx("cursor-pointer font-medium font-[SVN-Gilroy]", {
            "text-[#38499b]": activedTab === AccountTab.CHANGE_PASSWORD,
          })}
          onClick={() => handleTabClick(AccountTab.CHANGE_PASSWORD)}
        >
          Đổi mật khẩu
        </div>
      </div>
      {render()}
    </div>
  );
}

export default AccountUser;
